import { Link } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import { useReferrerState } from 'hooks'

import type { SeoSchedule } from 'services'

import { HowItWorksCard } from '../HowItWorksCard'
import { SectionNote } from '../shared'
import { scheduleLabel } from './seoModel'

const STORAGE_KEY = 'vera.seo.howItWorks.open'
// VRG-672 — the SEO reference moved with the page, out of the Analytics guide into GTM's.
const GUIDE = '/internal/docs/internal/internal-gtm'

/** The VRG-676 defaults, for the moment before the status has loaded. */
const DEFAULT_SLOT = { weekday: 'thursday', localTime: '14:00', timeZone: 'America/New_York' }

/**
 * The loop, as the jobs actually run it (cadences from seo.workers.ts / seo.settings.ts).
 * The slot wording reads the API's settings when they are loaded, so the panel cannot
 * describe a schedule the scheduler is not running.
 */
const steps = (schedule?: SeoSchedule): ReadonlyArray<{ title: string; body: string }> => {
  const slot = scheduleLabel(schedule ?? DEFAULT_SLOT, schedule?.intervalDays ?? 14)
  return [
  {
    title: '1. Data refreshes on its own',
    body: `Search Console syncs nightly. Rankings for Vera and every approved competitor run ${slot}, in every market (US, UK, France and Italy), and AI answers run in the same slot, three samples per prompt. Keyword volumes and trends refresh monthly, followed by the master re-score. Competitor gaps are re-mined monthly (approved Direct competitors, top 20, US, inside the credit cap). New competitors are proposed after every ranking check.`,
  },
  {
    title: '2. Recommendations are recomputed after each run',
    body:
      'Title rewrites, pages losing ground, rising terms with no page, new gap terms, cannibalisation, AI answer changes and proposals awaiting review. Each item carries its evidence and a suggested action. Once a quarter a strategy-reset item appears too.',
  },
  {
    title: '3. A review is sent after the US and UK cycle',
    body:
      'When both rankings cycles have collected every result and the AI answers have finished, usually within the hour, the review is built: what moved, the top five recommendations, wins and losses, and spend. It is emailed to the review list (and posted to Slack once a webhook is set), with a short written summary when Gemini is configured.',
  },
  {
    title: '4. A platform admin decides',
    body:
      'Approve, reject or snooze each recommendation on the Recommendations section. Nothing ships without an approval. Rejected items stay rejected; snoozed ones come back if they still apply.',
  },
  {
    title: '5. Ship, then measure',
    body:
      'An approved item becomes a tracked action: approved, in progress, shipped, measured. Marking it measured records the same metric again beside its value at approval, so the next cycle shows whether it worked.',
  },
  ]
}

const QUARTERLY = {
  title: 'Every quarter: reset the strategy',
  body:
    'Re-run keyword expansion, review the tracking rule and the markets, refresh the French and Italian term sets, and revisit the competitor set.',
}

/**
 * VRG-643 follow-up (Spencer, 2026-10-03) — "How this works": the SEO loop in five steps,
 * collapsible, closed by default and remembered per browser. The full reference is the
 * internal GTM guide; the terms are in the internal glossary. The card itself is the
 * shared `HowItWorksCard` (VRG-665 extracted it when the Affiliates tab needed the same one).
 */
export const SeoHowItWorks = ({ schedule }: { schedule?: SeoSchedule }) => {
  const referrer = useReferrerState('SEO & AEO')
  return (
    <HowItWorksCard
      id="seo-how-it-works"
      storageKey={STORAGE_KEY}
      summary="data, recommendations, review, approve, ship, measure"
      steps={[...steps(schedule), QUARTERLY]}
      footer={
        <SectionNote>
          Every job is capped before it spends: SE Ranking credits and LLM dollars per month, shown
          on Jobs and spend. The full reference is the{' '}
          <Link component={RouterLink} to={GUIDE} state={referrer}>
            GTM guide
          </Link>
          ; each term on this page is defined in the internal glossary.
        </SectionNote>
      }
    />
  )
}
